import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { type Tier, formatOrdinal, scaledDelta, tiedRanks } from '@topmeeps/shared'
import { useMemo, useState } from 'react'
import { ApiError } from '../api/client'
import {
  useDeletePlay,
  useDismissDuplicate,
  useGroup,
  useMergeDuplicate,
  usePlay,
  usePlayDuplicates,
} from '../api/hooks'
import type { DuplicateCandidate, PlayRatingImpact } from '../api/types'
import { ScreenShell } from '../components/ScreenShell'
import { formatDateTime } from '../datetime'
import {
  BackButton,
  GameThumb,
  MeepleAvatar,
  NumberCircle,
  Pill,
  ScorePad,
  ScorePadRow,
  Stamp,
  TierBadge,
  TopBar,
} from '../primitives'

// Server returns the raw CE delta; scale to display SR here.
const formatDelta = (rawDelta: number): string => {
  const delta = scaledDelta(rawDelta)
  if (delta === 0) return '±0 SR'
  return delta > 0 ? `+${delta} SR` : `${delta} SR`
}

// A tier change marks the play as a promotion, without naming the tier
// (tier names are deliberately de-emphasised across the app for now).
const tierTransitionLabel = (before: Tier | null, after: Tier | null): string | null =>
  after !== null && before !== after ? 'Promoted' : null

// Duplicate-play warnings are hidden for now. Detection and the
// merge/dismiss flow (DuplicatesSection below) are kept intact — flip
// this to re-surface the section.
const SHOW_DUPLICATE_WARNINGS = false

export const PlayDetailScreen = () => {
  const { id } = useParams({ from: '/play/$id' })
  const navigate = useNavigate()
  const play = usePlay(id)
  const deletePlay = useDeletePlay(play.data?.play.groupId ?? null)
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  const ordered = useMemo(
    () => [...(play.data?.participants ?? [])].sort((a, b) => a.rank - b.rank),
    [play.data],
  )
  const tiedPlaces = useMemo(() => tiedRanks(ordered.map((p) => p.rank)), [ordered])

  // Set by the record flow on save — drives the "just recorded" finish
  // treatment (success banner, explicit exits, no back-into-the-wizard).
  const { recorded } = useSearch({ from: '/play/$id' })
  const groupId = play.data?.play.groupId ?? null
  const group = useGroup(groupId ?? '')
  const exit = () =>
    groupId ? navigate({ to: '/group/$id', params: { id: groupId } }) : navigate({ to: '/home' })

  const onDelete = async () => {
    if (!play.data) return
    await deletePlay.mutateAsync(play.data.play.id)
    if (play.data.play.groupId) {
      navigate({ to: '/group/$id', params: { id: play.data.play.groupId } })
    } else {
      navigate({ to: '/home' })
    }
  }

  return (
    <ScreenShell>
      {/* No back arrow on the just-recorded screen — it would lead into
          the completed wizard. The bottom nav and "Go to {group}" are
          the ways out. */}
      <TopBar left={recorded ? undefined : <BackButton fallback={exit} />} title="Play" />

      {play.isLoading && <div style={{ padding: 16, color: 'var(--ink-3)' }}>Loading…</div>}

      {play.data && (
        <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {recorded && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 14px',
                background: 'var(--felt)',
                color: '#FFF8E8',
                borderRadius: 6,
                fontFamily: 'var(--font-display)',
                fontWeight: 700,
                fontSize: 15,
              }}
            >
              <span aria-hidden style={{ fontSize: 16 }}>
                ✓
              </span>
              Play recorded
            </div>
          )}
          <div
            className="felt-card"
            style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 12 }}
          >
            <GameThumb label={play.data.game.name} imageUrl={play.data.game.imageUrl} size={56} />
            <div style={{ flex: 1 }}>
              <div className="kicker">recorded</div>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 22 }}>
                {play.data.game.name}
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                {formatDateTime(play.data.play.playedAt)}
                {play.data.play.location ? ` · ${play.data.play.location}` : ''}
              </div>
            </div>
          </div>

          <div>
            <div className="kicker" style={{ padding: '0 4px 6px' }}>
              standings
            </div>
            <ScorePad>
              {ordered.map((p) => (
                <ScorePadRow key={p.id}>
                  <NumberCircle n={p.rank} winner={p.rank === 1} size={24} />
                  {p.displayName ? (
                    <MeepleAvatar
                      name={p.displayName}
                      hue={p.avatarHue ?? 0}
                      shape={p.avatarShape ?? 'meeple'}
                      size={32}
                      ghost={p.guestId !== null}
                    />
                  ) : (
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 4,
                        background: 'var(--paper-3)',
                      }}
                    />
                  )}
                  <div style={{ flex: 1 }}>
                    {p.handle ? (
                      <Link
                        to="/u/$handle"
                        params={{ handle: p.handle }}
                        style={{
                          fontWeight: 600,
                          fontSize: 14,
                          color: 'var(--ink)',
                          textDecoration: 'none',
                        }}
                      >
                        {p.displayName}
                      </Link>
                    ) : (
                      <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--ink-2)' }}>
                        {p.displayName ?? '(guest)'}
                      </div>
                    )}
                    {tiedPlaces.has(p.rank) && (
                      <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                        Tied for {formatOrdinal(p.rank)}
                      </div>
                    )}
                    {p.score != null && (
                      <div
                        style={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: 11,
                          color: 'var(--ink-3)',
                        }}
                      >
                        {p.score}
                      </div>
                    )}
                  </div>
                </ScorePadRow>
              ))}
            </ScorePad>
          </div>

          {SHOW_DUPLICATE_WARNINGS && (
            <DuplicatesSection playId={play.data.play.id} groupId={play.data.play.groupId} />
          )}

          <RatingImpactSection
            participants={play.data.participants}
            impact={play.data.ratingImpact}
            playGroupId={play.data.play.groupId}
          />

          {play.data.editAuthority !== 'none' && (
            <div
              className="felt-card"
              style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="kicker">edit window open</span>
                {play.data.editAuthority === 'admin' && <Pill color="gilt">admin</Pill>}
              </div>
              {!confirmingDelete ? (
                <div style={{ display: 'flex', gap: 8 }}>
                  <Link to="/play/$id/edit" params={{ id }} style={{ flex: 1 }}>
                    <button type="button" className="btn-ghost" style={{ width: '100%' }}>
                      Edit
                    </button>
                  </Link>
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => setConfirmingDelete(true)}
                    style={{
                      flex: 1,
                      color: 'var(--terra-ink)',
                      borderColor: 'var(--terra)',
                    }}
                  >
                    Delete
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>
                    Delete this play? Standings will be recomputed.
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      className="btn-ghost"
                      style={{ flex: 1 }}
                      onClick={() => setConfirmingDelete(false)}
                      disabled={deletePlay.isPending}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn-stamp"
                      style={{
                        flex: 1,
                        background: 'var(--terra-ink)',
                        boxShadow:
                          '0 1px 0 rgba(255,255,255,.25) inset, 0 -2px 0 rgba(0,0,0,.18) inset, 0 2px 0 #4a1f17',
                      }}
                      onClick={onDelete}
                      disabled={deletePlay.isPending}
                    >
                      {deletePlay.isPending ? 'Recomputing…' : 'Delete'}
                    </button>
                  </div>
                  {deletePlay.isError && (
                    <div style={{ fontSize: 12, color: 'var(--terra-ink)' }}>
                      {(deletePlay.error as Error).message}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {play.data.play.notes && (
            <div className="felt-card" style={{ padding: 12 }}>
              <div className="kicker">notes</div>
              <div style={{ fontSize: 13, color: 'var(--ink-2)', whiteSpace: 'pre-wrap' }}>
                {play.data.play.notes}
              </div>
            </div>
          )}

          {recorded && groupId && (
            <Link
              to="/group/$id"
              params={{ id: groupId }}
              className="btn-ghost"
              style={{ textAlign: 'center', textDecoration: 'none' }}
            >
              Go to {group.data?.name ?? 'your group'}
            </Link>
          )}
        </div>
      )}
    </ScreenShell>
  )
}

const DuplicatesSection = ({
  playId,
  groupId,
}: {
  playId: string
  groupId: string | null
}) => {
  const dups = usePlayDuplicates(playId)
  const dismiss = useDismissDuplicate(playId)
  const merge = useMergeDuplicate(playId, groupId)
  const [error, setError] = useState<string | null>(null)
  const [pendingMerge, setPendingMerge] = useState<string | null>(null)

  if (!dups.data || dups.data.length === 0) return null

  const onDismiss = async (other: DuplicateCandidate) => {
    setError(null)
    try {
      await dismiss.mutateAsync(other.playId)
    } catch (err) {
      setError(err instanceof ApiError ? err.friendlyMessage : 'Could not dismiss')
    }
  }

  const onMerge = async (other: DuplicateCandidate) => {
    setError(null)
    try {
      await merge.mutateAsync(other.playId)
      setPendingMerge(null)
    } catch (err) {
      setError(err instanceof ApiError ? err.friendlyMessage : 'Could not merge')
      setPendingMerge(null)
    }
  }

  return (
    <div
      style={{
        padding: 12,
        background: 'var(--gilt-bg)',
        border: '1px solid var(--gilt)',
        borderRadius: 6,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div className="kicker" style={{ color: 'var(--gilt-ink)' }}>
        possible duplicate · {dups.data.length} flagged
      </div>
      <div style={{ fontSize: 12, color: 'var(--ink-2)' }}>
        Within 4 hours, same game, overlapping rosters. Both plays still affect ratings until you
        merge or dismiss.
      </div>
      {dups.data.map((d) => {
        const isConfirming = pendingMerge === d.playId
        return (
          <div
            key={d.playId}
            style={{
              padding: 10,
              background: 'var(--paper)',
              border: '1px solid var(--line-soft)',
              borderRadius: 4,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Link
                  to="/play/$id"
                  params={{ id: d.playId }}
                  style={{
                    fontWeight: 600,
                    fontSize: 13,
                    color: 'var(--ink)',
                    textDecoration: 'none',
                  }}
                >
                  {d.gameName}
                </Link>
                <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
                  {formatDateTime(d.playedAt)} · {d.participantCount} players ·{' '}
                  {Math.round(d.overlapPct * 100)}% overlap
                </div>
              </div>
            </div>
            {isConfirming ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ fontSize: 12, color: 'var(--ink-2)' }}>
                  Merge: keep this play, soft-delete the other, ratings recompute.
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => setPendingMerge(null)}
                    style={{ flex: 1, fontSize: 11, padding: '6px 8px' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn-stamp"
                    onClick={() => onMerge(d)}
                    disabled={merge.isPending}
                    style={{
                      flex: 1,
                      fontSize: 11,
                      padding: '6px 8px',
                      background: 'var(--terra)',
                      borderColor: 'var(--terra)',
                    }}
                  >
                    {merge.isPending ? 'Merging…' : 'Yes, merge'}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => onDismiss(d)}
                  disabled={dismiss.isPending}
                  style={{ flex: 1, fontSize: 11, padding: '6px 8px' }}
                >
                  Not a duplicate
                </button>
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => setPendingMerge(d.playId)}
                  style={{ fontSize: 11, padding: '6px 8px' }}
                >
                  Merge into this
                </button>
              </div>
            )}
          </div>
        )
      })}
      {error && <div style={{ fontSize: 12, color: 'var(--terra-ink)' }}>{error}</div>}
    </div>
  )
}

type RatingImpactSectionProps = {
  participants: NonNullable<ReturnType<typeof usePlay>['data']>['participants']
  impact: PlayRatingImpact[]
  playGroupId: string | null
}

const RatingImpactSection = ({ participants, impact, playGroupId }: RatingImpactSectionProps) => {
  // Show in-scope deltas (per-group when grouped, else cross-group).
  const scoped = impact.filter((i) =>
    playGroupId === null ? i.groupId === null : i.groupId === playGroupId,
  )
  const byUser = new Map(scoped.map((i) => [i.userId, i]))
  const ordered = [...participants].sort((a, b) => a.rank - b.rank)

  if (scoped.length === 0) return null

  return (
    <div>
      <div className="kicker" style={{ padding: '0 4px 6px' }}>
        rating impact
      </div>
      <ScorePad>
        {ordered.map((p) => {
          const i = p.userId ? byUser.get(p.userId) : null
          if (!i) return null
          const positive = i.delta > 0
          const negative = i.delta < 0
          const promotion = tierTransitionLabel(i.tierBefore, i.tierAfter)
          return (
            <ScorePadRow key={p.id} style={{ alignItems: 'flex-start' }}>
              {p.displayName && (
                <MeepleAvatar
                  name={p.displayName}
                  hue={p.avatarHue ?? 0}
                  shape={p.avatarShape ?? 'meeple'}
                  size={32}
                />
              )}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {p.handle ? (
                    <Link
                      to="/u/$handle"
                      params={{ handle: p.handle }}
                      style={{
                        fontWeight: 600,
                        fontSize: 14,
                        color: 'var(--ink)',
                        textDecoration: 'none',
                      }}
                    >
                      {p.displayName}
                    </Link>
                  ) : (
                    <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--ink-2)' }}>
                      {p.displayName ?? '(guest)'}
                    </span>
                  )}
                  <TierBadge tier={i.tierAfter} score={i.muAfter} compact />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 13,
                      fontWeight: 600,
                      color: positive
                        ? 'var(--felt-ink)'
                        : negative
                          ? 'var(--terra-ink)'
                          : 'var(--ink-3)',
                    }}
                  >
                    {formatDelta(i.delta)}
                  </span>
                  {promotion && (
                    <Stamp color="var(--felt)" size={11}>
                      {promotion}
                    </Stamp>
                  )}
                </div>
              </div>
            </ScorePadRow>
          )
        })}
      </ScorePad>
    </div>
  )
}
